import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useFontSize } from '../../../../../context/FontSizeContext';

type Preset = 'sm' | 'md' | 'lg' | 'xl';
type FontFamilyPreset =
  | 'plus_jakarta_sans'
  | 'inter'
  | 'roboto'
  | 'poppins'
  | 'lato'
  | 'merriweather'
  | 'open_sans'
  | 'montserrat'
  | 'nunito'
  | 'source_sans_3'
  | 'playfair_display';

const FONT_FAMILY_OPTIONS: Array<{
  value: FontFamilyPreset;
  label: string;
  sampleFamily: string;
  sampleText: string;
}> = [
  {
    value: 'plus_jakarta_sans',
    label: 'Plus Jakarta Sans (recommended)',
    sampleFamily:
      '"Plus Jakarta Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial',
    sampleText:
      'Plus Jakarta Sans: contemporary UI type — clear, friendly, and widely used for modern commerce.',
  },
  {
    value: 'inter',
    label: 'Inter',
    sampleFamily:
      '"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial',
    sampleText: 'Inter: neutral, dense, and familiar for dashboards.',
  },
  {
    value: 'roboto',
    label: 'Roboto',
    sampleFamily:
      '"Roboto", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial',
    sampleText: 'Roboto: neutral and familiar interface readability.',
  },
  {
    value: 'poppins',
    label: 'Poppins',
    sampleFamily:
      '"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial',
    sampleText: 'Poppins: geometric and prominent display feel.',
  },
  {
    value: 'lato',
    label: 'Lato',
    sampleFamily:
      '"Lato", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial',
    sampleText: 'Lato: warm, professional and business-friendly.',
  },
  {
    value: 'merriweather',
    label: 'Merriweather',
    sampleFamily: '"Merriweather", Georgia, Cambria, "Times New Roman", Times, serif',
    sampleText: 'Merriweather: classic serif tone for editorial style.',
  },
  {
    value: 'open_sans',
    label: 'Open Sans',
    sampleFamily: '"Open Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial',
    sampleText: 'Open Sans: highly readable for dense dashboards.',
  },
  {
    value: 'montserrat',
    label: 'Montserrat',
    sampleFamily: '"Montserrat", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial',
    sampleText: 'Montserrat: contemporary with strong headings.',
  },
  {
    value: 'nunito',
    label: 'Nunito',
    sampleFamily: '"Nunito", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial',
    sampleText: 'Nunito: rounded and friendly customer experience.',
  },
  {
    value: 'source_sans_3',
    label: 'Source Sans 3',
    sampleFamily: '"Source Sans 3", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial',
    sampleText: 'Source Sans 3: enterprise-grade clarity for operations.',
  },
  {
    value: 'playfair_display',
    label: 'Playfair Display',
    sampleFamily: '"Playfair Display", Georgia, Cambria, "Times New Roman", Times, serif',
    sampleText: 'Playfair Display: premium serif for branded storytelling.',
  },
];

function labelFor(p: Preset): string {
  switch (p) {
    case 'sm': return 'Small';
    case 'md': return 'Default';
    case 'lg': return 'Large';
    case 'xl': return 'Extra large';
    default: return 'Default';
  }
}

export default function DisplaySettingsPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const {
    effectivePreset,
    globalPreset,
    userPreset,
    useGlobal,
    setUserPreset,
    setUseGlobal,
    setGlobalPreset,
    globalFontFamily,
    setGlobalFontFamily,
    canSetGlobal,
  } = useFontSize();

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.display_title', { defaultValue: 'Display' })}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.display_subtitle', { defaultValue: 'Adjust text size across the whole application.' })}
          </p>
        </section>

        <section className="workspace-panel">
          <div className="grid gap-5 xl:grid-cols-2">
            <div className="rounded-xl border border-slate-100 bg-white/60 p-4">
              <div className="mb-3 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    {t('backoffice.display_effective_title', { defaultValue: 'Current font size' })}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {t('backoffice.display_effective_help', { defaultValue: 'This is what you see right now.' })}
                  </p>
                </div>
                <span className="rounded-full bg-slate-900/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-800">
                  {effectivePreset.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="rounded-xl border border-slate-100 bg-white/60 p-4">
              <div className="mb-3">
                <h2 className="text-sm font-semibold text-slate-900">
                  {t('backoffice.display_personal_title', { defaultValue: 'Your preference' })}
                </h2>
                <p className="text-xs text-slate-500">
                  {t('backoffice.display_personal_help', { defaultValue: 'Use the global default or override it for your account.' })}
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void setUseGlobal(true)}
                    className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition ${
                      useGlobal ? 'bg-slate-900 text-white' : 'border border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                    }`}
                  >
                    {t('backoffice.display_use_global', { defaultValue: 'Use global' })}
                  </button>
                  <button
                    type="button"
                    onClick={() => void setUseGlobal(false)}
                    className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition ${
                      !useGlobal ? 'bg-slate-900 text-white' : 'border border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                    }`}
                  >
                    {t('backoffice.display_use_custom', { defaultValue: 'Use custom' })}
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {t('backoffice.display_custom_label', { defaultValue: 'Custom size' })}
                  </label>
                  <select
                    value={userPreset}
                    disabled={useGlobal}
                    onChange={(e) => void setUserPreset(e.target.value as Preset)}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70 disabled:opacity-60"
                  >
                    {(['sm', 'md', 'lg', 'xl'] as Preset[]).map((p) => (
                      <option key={p} value={p}>
                        {labelFor(p)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-100 bg-white/60 p-4 xl:col-span-2">
              <div className="mb-3 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    {t('backoffice.display_global_title', { defaultValue: 'Global default' })}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {t('backoffice.display_global_help', { defaultValue: 'Applies to users who choose “Use global”.' })}
                  </p>
                </div>
                <span className="rounded-full bg-slate-900/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-800">
                  {globalPreset.toUpperCase()}
                </span>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-slate-600">
                  {canSetGlobal
                    ? t('backoffice.display_global_can_edit', { defaultValue: 'You can update the global default.' })
                    : t('backoffice.display_global_readonly', { defaultValue: 'You do not have permission to change the global default.' })}
                </div>
                <select
                  value={globalPreset}
                  disabled={!canSetGlobal}
                  onChange={(e) => void setGlobalPreset(e.target.value as Preset)}
                  className="w-full max-w-xs rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70 disabled:opacity-60"
                >
                  {(['sm', 'md', 'lg', 'xl'] as Preset[]).map((p) => (
                    <option key={p} value={p}>
                      {labelFor(p)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rounded-xl border border-slate-100 bg-white/60 p-4">
              <div className="mb-3 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    {t('backoffice.display_font_family_title', { defaultValue: 'Global font family' })}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {t('backoffice.display_font_family_help', { defaultValue: 'Choose the main font used across the app.' })}
                  </p>
                </div>
              </div>

              <div className="grid gap-3">
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {FONT_FAMILY_OPTIONS.map((x) => {
                    const active = x.value === globalFontFamily;
                    return (
                      <button
                        type="button"
                        key={x.value}
                        onClick={() => void setGlobalFontFamily(x.value)}
                        disabled={!canSetGlobal}
                        className={`rounded-lg border px-3 py-2 text-left transition ${
                          active
                            ? 'border-brand-500 bg-brand-50/60 ring-2 ring-brand-300/60'
                            : 'border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50/20'
                        } disabled:cursor-not-allowed disabled:opacity-70`}
                      >
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                          {x.label}
                        </p>
                        <p
                          className="mt-1 text-sm text-slate-900"
                          style={{ fontFamily: x.sampleFamily }}
                        >
                          {x.sampleText}
                        </p>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500">
                  Click any card to apply it instantly as global font family.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </MainLayout>
  );
}

